import type { QuestGoal, Skill, SkillRequirement } from '../types';

/**
 * ⭐⭐ **WHAT EVERY ACTION TYPE IS, IN THE USER'S WORDS** — the teaching content
 * behind step 4's walkthrough (`BuildCascadeSequence`), its step copy, and
 * anywhere else that has to explain the cascade.
 *
 * ⚠️⚠️ **DATA, NOT PROSE IN A COMPONENT.** The same eleven explanations are owed
 * to the tour, to the step page and to whatever teaches this next; three copies
 * would drift and the user would meet two different definitions of a Quest. Same
 * reason `DOMAIN_DESCRIPTIONS` was lifted out of `DomainAbout` in R3e.
 *
 * ⚠️ This describes the SHAPE of each type. It is not a second source of truth
 * for behaviour — `linkable` below is asserted against `QuestGoal['linkType']`
 * by a test, because teaching copy that promises a link the model does not allow
 * is a lie the compiler cannot see.
 */

/**
 * How long the type lives, as the walkthrough presents it (owner, 2026-08-11).
 *
 * ⚠️ This is a TEACHING horizon, not a stored field. Nothing in the data model
 * carries it — a Quest has no duration column — so it exists to give the tour
 * three shelves to put eleven things on, and for no other purpose.
 * ⚠️ Do not confuse with the dead `GoalTerm` in `types/index.ts`: that is an
 * unexported, unreferenced leftover on a `DomainGoal` nothing builds.
 */
export type ActionHorizon = 'long' | 'medium' | 'short';

/** ⚠️ The teaching order, declared once so the tour and the tests read one list. */
export const ACTION_HORIZONS_ORDER: readonly ActionHorizon[] = ['long', 'medium', 'short'];

/**
 * ⭐⭐ `ends` — WHAT FINISHES A THING ON THIS BAND, and the reason the tour can
 * draw a timeline without inventing a single duration. Nothing in the data model
 * carries a length, so "two to six weeks" on a band would be a claim the app
 * cannot support and a lie for half the people reading it. What DOES separate
 * the three is what ends them, and that is both true and the sharper lesson.
 */
export const HORIZON_META: Record<ActionHorizon, { label: string; blurb: string; ends: string }> = {
  /**
   * ⚠️ IT USED TO SAY "the direction". Step 4's hub puts these bands under an
   * arc labelled Purpose → Mission → Vision, which is what the product's own
   * vocabulary brief calls the direction — so that one word named both the top
   * of the page and a bucket near the bottom of it.
   */
  long:   { label: 'Long term',   ends: 'Does not end',
            blurb: 'Never finished. A Quest holds Goals; an Ability holds Skills.' },
  medium: { label: 'Medium term', ends: 'Ends when it is finished',
            blurb: 'The vehicles. Bigger than a single act, smaller than a direction.' },
  short:  { label: 'Short term',  ends: 'Ends when you do it',
            blurb: 'The work you actually tick. This is where a cascade touches your day.' },
};

export interface ActionTypeSpec {
  /** ⚠️ Matches the `InboxDestination` id where one exists — never a new spelling. */
  id: string;
  label: string;
  horizon: ActionHorizon;
  /** One line: what it IS. */
  whatItIs: string;
  /** The shape a good one takes — the rule, not an example. */
  format: string;
  /** A worked example, written as the user would see it. */
  example: string;
  /**
   * Where it lives, when it cannot stand alone. `null` = standalone.
   * ⚠️ Mirrors `ATTACH_SPEC` in `types/inbox.ts` for the three nested types.
   */
  livesInside: string | null;
  /**
   * Whether a Goal can be LINKED to it — asserted against `QuestGoal.linkType`.
   * ⚠️⚠️ `derives: false` with `linkable: true` is the Ability case and it is
   * REAL: you can link it, but `questGoalLink` has no completion signal for an
   * ability, so the goal stays on its manual slider. The tour must say so, or a
   * user links one and waits for a number that never moves.
   */
  linkable: boolean;
  derives: boolean;
}

/**
 * ⚠️ Order is the TEACHING order — long → medium → short, direction before the
 * work. It is not the order of `CLASSIFY_DESTINATIONS`, which is the Inbox's
 * grouping for a different job.
 */
export const ACTION_TYPES: readonly ActionTypeSpec[] = [
  {
    id: 'quest', label: 'Quest', horizon: 'long',
    whatItIs: 'A pursuit inside one domain. It holds Goals, and it is never ticked off itself.',
    format: 'Name the RESULT, plainly. No numbers, no date — if you can measure it, it is a Goal.',
    example: 'Become genuinely healthier',
    livesInside: null, linkable: false, derives: false,
  },
  {
    /**
     * ⚠️⚠️ **MEDIUM, NOT LONG** (2026-09-18). A Goal sat on the long band, whose
     * own blurb reads "these are never ticked off" — and a Goal is ticked off:
     * its achievement badge's verb is literally "completed". One member
     * contradicted the shelf it was on. Moved here, where "bigger than a single
     * act, smaller than a direction" describes it exactly, and the long band is
     * left holding the two things that genuinely never finish.
     * ⚠️ Safe to move because the horizon is a TEACHING device with nothing
     * stored behind it — see `ActionHorizon` above.
     */
    id: 'goal', label: 'Goal', horizon: 'medium',
    whatItIs: 'The measurable rung. It reads its progress off whatever you link it to.',
    format: 'Four fields: a verb, a thing, a quantity, a date. The app writes the sentence.',
    example: 'Build unbroken bodyweight squat reps in a single set (50) by 1 Sep 2026',
    livesInside: 'Quest', linkable: false, derives: false,
  },
  {
    id: 'ability', label: 'Ability', horizon: 'long',
    whatItIs: 'A capability you are building — a skill tree that holds Skills.',
    format: 'A noun, not a task. The thing you want to be able to do.',
    example: 'Calisthenics',
    livesInside: null, linkable: true, derives: false,
  },
  {
    id: 'project', label: 'Project', horizon: 'medium',
    whatItIs: 'A container of tasks working toward one outcome, with a finish line.',
    format: 'Name the finished state, so you can tell when it is over.',
    example: 'Home gym set up',
    livesInside: null, linkable: true, derives: true,
  },
  {
    id: 'program', label: 'Program', horizon: 'medium',
    whatItIs: 'A designed plan with modules and scheduled slots — you follow it rather than invent it.',
    format: 'A named plan with a duration.',
    example: '12-week beginner strength block',
    livesInside: null, linkable: true, derives: true,
  },
  {
    id: 'skill', label: 'Skill', horizon: 'medium',
    whatItIs: 'One rung of an Ability, at one of seven levels from foundation to mastery.',
    format: 'The specific thing you can do, plus the level it sits at.',
    example: 'Bodyweight squat — developing',
    livesInside: 'Ability', linkable: false, derives: false,
  },
  {
    id: 'task', label: 'Task', horizon: 'short',
    whatItIs: 'One thing, done once, then finished.',
    format: 'A verb and an object. Small enough to finish in one go.',
    example: 'Book the physio appointment',
    livesInside: null, linkable: true, derives: true,
  },
  {
    id: 'event', label: 'Event', horizon: 'short',
    whatItIs: 'Something that happens at a set time, on the calendar.',
    format: 'The act, plus when. It either happened or it did not — there is no partial.',
    example: 'Gym induction — Tuesday, 7pm',
    livesInside: null, linkable: true, derives: true,
  },
  {
    id: 'routine', label: 'Routine', horizon: 'short',
    whatItIs: 'An ordered sequence of steps, each with a duration.',
    format: 'The steps in the order you do them.',
    example: 'Morning: water → mobility → ten-minute walk',
    livesInside: null, linkable: true, derives: true,
  },
  {
    id: 'requirement', label: 'Requirement', horizon: 'short',
    whatItIs: 'The evidence that proves a Skill rung is earned.',
    format: 'What you would have to show. Checkable by someone else.',
    example: 'Film 20 unbroken reps at full depth',
    livesInside: 'Skill', linkable: false, derives: false,
  },
  {
    /**
     * ⚠️ **LAST OF THE SHORT GROUP ON PURPOSE** (owner, 2026-09-18). Step 4's hub
     * lays each band out two to a row and spans the odd last block across both,
     * so the final member of a five-type band is the one drawn wide. A Habit is
     * the short band's centre of gravity — it is what a Mission moves on, and the
     * likeliest thing a reader will link a Goal to — where a Requirement is the
     * most enclosed type on the map, reachable only from inside a Skill.
     * ⚠️ Order within a band carries no behaviour; the band itself is the
     * teaching device. See `ACTION_TYPES` above.
     */
    id: 'habit', label: 'Habit', horizon: 'short',
    whatItIs: 'Something you repeat on a schedule, tracked as a streak.',
    format: 'The act, plus how often. The repetition is the point, not any one instance.',
    example: 'Ten minutes of mobility, every weekday',
    livesInside: null, linkable: true, derives: true,
  },
];

/**
 * ⭐ The SEVEN skill levels, in order.
 * ⚠️⚠️ `'foundation'` is ALSO the first rung of the domain readiness ladder and a
 * word used elsewhere in the app — never bulk-replace it.
 */
export const SKILL_LEVELS: readonly Skill['level'][] = [
  'foundation', 'developing', 'intermediate', 'proficient', 'advanced', 'expert', 'mastery',
];

export const actionType = (id: string): ActionTypeSpec | undefined =>
  ACTION_TYPES.find(t => t.id === id);

/**
 * The indefinite article for a type label — `a Task`, but `an Event`.
 *
 * ⚠️⚠️ **TWO OF THE ELEVEN LABELS BEGIN WITH A VOWEL** (Ability, Event), so any
 * sentence that hardcodes "a" before a type name is wrong about a fifth of the
 * time it renders. Caught live reading the drill's own verdict line — it said
 * *"Yes — a Event."* — not by a test, because a test written from the same
 * assumption would have pinned the same mistake.
 *
 * ⚠️ Lives here, with the labels, so every surface that names a type reaches for
 * the same rule instead of each one deciding afresh.
 */
export const indefiniteArticle = (label: string): 'a' | 'an' =>
  /^[aeiou]/i.test(label) ? 'an' : 'a';

export const actionTypesByHorizon = (h: ActionHorizon): ActionTypeSpec[] =>
  ACTION_TYPES.filter(t => t.horizon === h);

/* ══════════════════════════════════════════════════════════════════════════════
   The pick-the-type drill (owner, 2026-08-11)
   ══════════════════════════════════════════════════════════════════════════════ */

/**
 * One phrasing to sort onto the right type.
 *
 * ⭐⭐ **WHY A DRILL AND NOT ELEVEN MORE TEXT FIELDS.** The tour already rehearses
 * *writing to a format* — that is what the Quest and Goal pages do. For the other
 * nine types the skill that actually matters is **knowing which one to reach
 * for**: a user who cannot tell a Habit from an Event does not have a phrasing
 * problem. So the rehearsal is recognition, and it costs no new pages — it sits
 * on the shelf page that just explained those types.
 */
export interface TypeDrill {
  /**
   * The phrasing shown to the reader.
   * ⚠️⚠️ **NEVER a type's own `example`.** The expanded row on the same page
   * shows that string, so reusing it turns the drill into a lookup — the reader
   * matches two identical lines and learns nothing. A test asserts they are all
   * distinct from every `example`.
   * ⚠️ Deliberately drawn from OTHER walks of life than the fitness worked
   * example the rest of the tour uses. If every prompt were a gym one, the
   * reader would learn *"squats are a Habit"* rather than *"a thing that repeats
   * on a schedule is a Habit"* — the shape is the lesson, not the subject.
   */
  prompt: string;
  /** The id of the type it is. ⚠️ A real `ACTION_TYPES` id — pinned by a test. */
  answer: string;
  /** One line: what in the phrasing gives it away. Shown after any pick. */
  why: string;
}

/**
 * ⏳ **OWNER CONTENT — drafted 2026-08-11, expected to be edited.**
 *
 * ⚠️⚠️ **ONE DRILL PER TYPE, AND A TEST PINS THAT.** Coverage is the whole point
 * of the feature: the gap being closed is *"eight types are explained but never
 * rehearsed"*, and a list that drifts to nine covered types reopens it silently.
 *
 * ⭐ **The shelf is DERIVED, never declared** (`typeDrillsByHorizon` reads the
 * answer's own horizon), so a drill cannot be shown on a page whose types do not
 * include its answer — and the wrong-answer chips are that shelf's real types,
 * so there is no distractor list to keep in step with anything.
 */
export const TYPE_DRILLS: readonly TypeDrill[] = [
  // ── Long term ────────────────────────────────────────────────────────────
  {
    prompt: 'Be someone my family can rely on',
    answer: 'quest',
    why: 'A result named plainly, with nothing to measure and no date to hit.',
  },
  {
    prompt: 'Public speaking',
    answer: 'ability',
    why: 'A capability, named as a noun. It holds Skills; it is not itself finished.',
  },

  // ── Medium term ──────────────────────────────────────────────────────────
  {
    prompt: 'Read 24 books by 31 December',
    answer: 'goal',
    why: 'A verb, a thing, a quantity and a date. If you can measure it, it is a Goal.',
  },
  {
    prompt: 'Kitchen renovated',
    answer: 'project',
    why: 'A finished state you can stand in front of and call done.',
  },
  {
    prompt: '8-week meditation course',
    answer: 'program',
    why: 'A designed plan with a duration — you follow it rather than invent it.',
  },
  {
    prompt: 'Conversational Spanish — intermediate',
    answer: 'skill',
    why: 'One rung of a wider ability, sitting at one of the seven levels.',
  },

  // ── Short term ───────────────────────────────────────────────────────────
  {
    prompt: 'Cancel the old insurance policy',
    answer: 'task',
    why: 'One thing, done once, then it is finished.',
  },
  {
    prompt: 'Journal for five minutes each night',
    answer: 'habit',
    why: 'It repeats on a schedule. The repetition is the point, not any one night.',
  },
  {
    prompt: 'Dentist — Thursday, 9am',
    answer: 'event',
    why: 'It happens at a set time. It either happened or it did not.',
  },
  {
    prompt: 'Evening: tidy up → lay out clothes → read',
    answer: 'routine',
    why: 'Ordered steps, run in sequence, each with its own place in the run.',
  },
  {
    prompt: 'Hold a five-minute conversation, unscripted',
    answer: 'requirement',
    why: 'The evidence that proves a Skill rung is earned — checkable by someone else.',
  },
];

/**
 * The drills belonging to one shelf, in declaration order.
 *
 * ⚠️ Reads the ANSWER'S horizon rather than a `horizon` field on the drill —
 * two fields that had to agree would eventually disagree, and the failure is
 * silent (a drill whose right answer is not among the chips offered).
 */
export const typeDrillsByHorizon = (h: ActionHorizon): TypeDrill[] =>
  TYPE_DRILLS.filter(d => actionType(d.answer)?.horizon === h);

/**
 * The drill for one type.
 *
 * ⭐⭐ **EVERY TYPE'S PRACTICE, WITHOUT WRITING ELEVEN OF THEM.** Step 4's hub
 * offers a rehearsal on each block, and for nine of the eleven the skill that
 * actually matters is knowing which one to reach for — a user who cannot tell a
 * Habit from an Event does not have a phrasing problem. The drill already
 * authored for each type IS that rehearsal, so the hub asks it where the type is
 * explained rather than collecting them into a quiz at the end.
 * ⚠️ One per type is pinned by a test, which is what makes "every block has a
 * practice" true rather than nearly true.
 */
export const drillFor = (typeId: string): TypeDrill | undefined =>
  TYPE_DRILLS.find(d => d.answer === typeId);

/**
 * ⚠️⚠️ **THE ONE FACT THE WHOLE STEP TURNS ON.** A Goal linked to something reads
 * its progress from that thing's real completions, counted from the moment the
 * link was made — not from its history. An UNLINKED goal is a number you keep by
 * hand. Stated here so the tour, the step copy and the panel cannot each phrase
 * it differently.
 */
export const LINK_PROMISE =
  'A linked Goal moves on work you actually record — anywhere in the app. Counting starts when you link it, not before.';

/** Every link type the model accepts, for the test that pins the copy to it. */
export type GoalLinkType = NonNullable<QuestGoal['linkType']>;

/* ══════════════════════════════════════════════════════════════════════════════
   The second linker
   ══════════════════════════════════════════════════════════════════════════════ */

/**
 * ⭐⭐ **A GOAL IS NOT THE ONLY THING THAT LINKS** (owner, 2026-09-18).
 *
 * `linkable` and `derives` above answer one question — *can a GOAL point at
 * this?* — and reading them as the whole story cost the map a real edge: a
 * Requirement can point at something you already do and tick itself off when
 * that work is recorded, exactly the mechanic the step spends its time
 * teaching. The hub showed a Requirement as a leaf with one relation (it lives
 * in a Skill), which is not what the app does.
 *
 * ⚠️⚠️ **PINNED TO THE MODEL, NOT TO THE MODAL.** `SkillRequirement['linkType']`
 * is the truth; a test asserts this list is exactly that union, so a seventh
 * option added to the form cannot leave the teaching copy behind — the same
 * guard `GoalLinkType` gives the Goal's list.
 */
export type RequirementLinkType = NonNullable<SkillRequirement['linkType']>;

/** Every target the model accepts, in the order the hub shows them. */
export const REQUIREMENT_LINKS: readonly RequirementLinkType[] =
  ['task', 'habit', 'routine', 'project', 'program', 'folder'];

/**
 * The ones that are ALSO blocks on the map — derived, never a second list.
 *
 * ⚠️⚠️ `folder` is a task FOLDER: a container in the Tasks hub, not one of the
 * eleven and not a block here. Drawing it would invent a twelfth type; omitting
 * it silently would make the map disagree with the form the user is looking at.
 * So it is filtered out of the highlight and said in words instead.
 */
export const REQUIREMENT_LINK_TYPES: readonly string[] =
  REQUIREMENT_LINKS.filter(id => ACTION_TYPES.some(t => t.id === id));

export const REQUIREMENT_LINK_PROMISE =
  'Point it at work you already record and it ticks itself off when that work lands. Counting starts when you link it, not before.';

/** ⚠️ Said plainly, because the form offers it and this map cannot draw it. */
export const REQUIREMENT_LINK_FOLDER_NOTE =
  'A folder of tasks works too. That is a container in Tasks, not an action type, so it has no block here.';
